import { IconContext, type IconProps as PhosphorIconProps } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { icons, type IconName } from "./icons/registry";
import { cn } from "@/lib/cn";

export type { IconName };

interface IconProps extends Omit<PhosphorIconProps, "weight" | "size" | "ref"> {
  name: IconName;
  /** 16 dense/inline · 20 default UI · 24 nav and feature. Stick to the 4px scale. */
  size?: 16 | 20 | 24 | 32 | 40;
  "aria-label"?: string;
}

/**
 * The only way icons enter feature code. Never render a raw <svg> or import a
 * Phosphor component directly — the registry is what keeps one family and one
 * weight across the app.
 */
export function Icon({ name, size = 20, className, ...props }: IconProps) {
  const Glyph = icons[name];
  return (
    <Glyph
      size={size}
      className={cn("shrink-0", className)}
      aria-hidden={props["aria-label"] ? undefined : true}
      {...props}
    />
  );
}

/**
 * Sets the stroke weight once for the whole tree. Regular reads correctly at
 * 16–24px and keeps icon weight consistent with Geist's text weight.
 */
export function IconProvider({ children }: { children: ReactNode }) {
  return <IconContext.Provider value={{ weight: "regular" }}>{children}</IconContext.Provider>;
}
